import React, { useRef, useState } from "react";
import { ImageUp, Search, SlidersHorizontal, Type, X } from "lucide-react";
import Button from "../ui/Button";
import { SelectField, TextField } from "../ui/Field";
import { OFFICE_NAMES } from "../../lib/format";
import { logoPreviewUrl } from "../../lib/logoImage";

/**
 * The search console — the single most important element on the landing
 * page, and the only place the brass accent appears there.
 *
 * Two modes: by name, and by logo. A logo search reads the words out of the
 * image (OCR) and then runs the same register search as a typed name.
 */

// Every office/country we have a name for, A–Z; the common ones are pinned to
// the top so they don't need scrolling for.
const PINNED_OFFICES = ["IN", "US", "GB", "EM", "WO", "CN", "JP", "DE", "FR"];

const JURISDICTIONS = [
  { value: "", label: "All jurisdictions" },
  ...[
    ...PINNED_OFFICES,
    ...Object.keys(OFFICE_NAMES)
      .filter((code) => code !== "EU" && !PINNED_OFFICES.includes(code))
      .sort((a, b) => OFFICE_NAMES[a].localeCompare(OFFICE_NAMES[b])),
  ].map((code) => ({
    value: code,
    label: `${OFFICE_NAMES[code] || code} (${code})`,
  })),
];

const SOURCES = [
  { value: "euipo", label: "EUIPO first, Signa as backup" },
  { value: "signa", label: "Signa only" },
];

export default function SearchConsole({
  onSearch,
  onLogoSearch,
  loading,
  initialQuery,
}) {
  const [mode, setMode] = useState("name");
  const [wordMark, setWordMark] = useState(initialQuery?.wordMark || "");
  const [niceClasses, setNiceClasses] = useState(
    (initialQuery?.niceClasses || []).join(", "),
  );
  const [jurisdiction, setJurisdiction] = useState(
    initialQuery?.jurisdiction || "",
  );
  const [applicantName, setApplicantName] = useState(
    initialQuery?.applicantName || "",
  );
  const [description, setDescription] = useState(
    initialQuery?.description || "",
  );
  const [source, setSource] = useState("euipo");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const fileInputRef = useRef(null);

  const canSubmit =
    mode === "name" ? Boolean(wordMark.trim()) : Boolean(logoFile);

  function buildPayload() {
    return {
      wordMark: wordMark.trim(),
      niceClasses: niceClasses
        .split(",")
        .map((c) => c.trim())
        .filter(Boolean),
      jurisdiction: jurisdiction || undefined,
      applicantName: applicantName.trim() || undefined,
      description: description.trim() || undefined,
      source,
    };
  }

  function handleSubmit(event) {
    event.preventDefault();
    if (!canSubmit || loading) return;
    if (mode === "logo")
      onLogoSearch?.({ file: logoFile, options: buildPayload() });
    else onSearch(buildPayload());
  }

  async function handleFileChange(event) {
    const file = event.target.files?.[0];
    if (!file) return;
    setLogoFile(file);
    setLogoPreview(null);
    try {
      // A data: URL, not a blob: one — see lib/logoImage.js for why.
      setLogoPreview(await logoPreviewUrl(file));
    } catch {
      // No thumbnail is fine; the file name still shows and search still works.
    }
  }

  function clearLogo() {
    setLogoFile(null);
    setLogoPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  const modeButton = (value, Icon, label) => (
    <button
      type="button"
      onClick={() => setMode(value)}
      aria-pressed={mode === value}
      className={`inline-flex items-center gap-1.5 rounded px-3.5 py-1.5 text-sm font-medium transition-colors ${
        mode === value
          ? "bg-white text-ink-900 shadow-raised"
          : "text-ink-600 hover:text-ink-900"
      }`}
    >
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
      {label}
    </button>
  );

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-soft border border-ink-200/70 bg-white p-5 shadow-console sm:p-7"
    >
      <div className="mb-5 inline-flex rounded bg-paper-sunken p-1">
        {modeButton("name", Type, "By name")}
        {modeButton("logo", ImageUp, "By logo")}
      </div>

      {mode === "name" ? (
        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <Search
              className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-ink-400"
              aria-hidden="true"
            />
            <input
              type="text"
              value={wordMark}
              onChange={(e) => setWordMark(e.target.value)}
              placeholder="Type a trade mark name"
              aria-label="Trade mark name"
              className="control h-14 pl-12 text-base"
              required
            />
          </div>
          <Button
            type="submit"
            variant="seal"
            size="lg"
            icon={Search}
            loading={loading}
            disabled={!canSubmit}
            className="h-14 sm:w-44"
          >
            {loading ? "Searching" : "Search"}
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-3 sm:flex-row">
          <label
            htmlFor="logo-upload"
            className="flex h-14 flex-1 cursor-pointer items-center gap-3 rounded border-2 border-dashed border-ink-200 bg-paper px-4 transition-colors hover:border-[#8f5eeb]-400 hover:bg-[#8f5eeb]-100/40"
          >
            {logoPreview ? (
              <img
                src={logoPreview}
                alt=""
                className="h-9 w-9 shrink-0 rounded border border-ink-200 bg-white object-contain"
              />
            ) : (
              <ImageUp
                className="h-5 w-5 shrink-0 text-ink-400"
                aria-hidden="true"
              />
            )}
            <span className="truncate text-sm text-ink-600">
              {logoFile
                ? logoFile.name
                : "Drop a logo here, or click to choose a file"}
            </span>
            {logoFile && (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  clearLogo();
                }}
                aria-label="Remove the uploaded logo"
                className="ml-auto shrink-0 rounded p-1 text-ink-400 hover:bg-ink-100 hover:text-ink-700"
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            )}
            <input
              id="logo-upload"
              ref={fileInputRef}
              type="file"
              accept="image/*,.jfif"
              onChange={handleFileChange}
              className="sr-only"
            />
          </label>
          <Button
            type="submit"
            variant="seal"
            size="lg"
            icon={ImageUp}
            loading={loading}
            disabled={!canSubmit}
            className="h-14 sm:w-44"
          >
            {loading ? "Reading logo" : "Search by logo"}
          </Button>
        </div>
      )}

      <button
        type="button"
        onClick={() => setShowAdvanced((v) => !v)}
        aria-expanded={showAdvanced}
        className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-ink-600 hover:text-ink-900"
      >
        <SlidersHorizontal className="h-3.5 w-3.5" aria-hidden="true" />
        {showAdvanced ? "Hide search options" : "More search options"}
      </button>

      {showAdvanced && (
        <div className="mt-4 grid grid-cols-1 gap-4 border-t border-ink-200 pt-5 sm:grid-cols-2 lg:grid-cols-3">
          <TextField
            label="Nice classes"
            value={niceClasses}
            onChange={(e) => setNiceClasses(e.target.value)}
            placeholder="9, 42"
            hint="Separate with commas"
          />
          <SelectField
            label="Jurisdiction"
            value={jurisdiction}
            onChange={(e) => setJurisdiction(e.target.value)}
            options={JURISDICTIONS}
          />
          <TextField
            label="Applicant"
            value={applicantName}
            onChange={(e) => setApplicantName(e.target.value)}
            placeholder="Company or person"
          />
          <TextField
            label="Goods and services"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What you'll sell under this mark"
            hint="Improves conflict scoring"
            className="sm:col-span-2"
          />
          <SelectField
            label="Register source"
            value={source}
            onChange={(e) => setSource(e.target.value)}
            options={SOURCES}
          />
        </div>
      )}
    </form>
  );
}
